import React, { useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ReactComponent as DropdownArrow } from "../../assets/icons/dropdownArrow.svg";
import {
  PREFERENCE_PAGE_SIZE,
  PreferenceGroup,
  PreferenceOption,
  collectionBuildBorderClassName,
  collectionBuildDividerClassName,
  preferenceGridClassName,
  preferenceRecommendedClassName,
} from "./collectionBuildConstants";

const preferencePagerArrowClassName = `box-border flex h-8 w-8 shrink-0 items-center justify-center rounded border bg-white text-base leading-none focus:outline-none disabled:cursor-not-allowed disabled:border-[#E2E2E2] disabled:text-[#E2E2E2] enabled:border-[#737373] enabled:text-[#737373] enabled:hover:bg-lightGray`;

const PreferencePageSelect: React.FC<{
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}> = ({ page, pageCount, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        aria-label="Preference page"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen(current => !current)}
        className={`relative box-border flex h-8 min-w-[2.75rem] items-center rounded border ${collectionBuildBorderClassName} bg-white pl-2.5 pr-6 font-avenir text-xs leading-none text-secondary hover:bg-lightGray focus:outline-none`}
      >
        {page}
        <span className="pointer-events-none absolute inset-y-0 right-1.5 flex items-center">
          <span
            className={`transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          >
            <DropdownArrow className="h-2 w-2" />
          </span>
        </span>
      </button>
      {open ? (
        <div
          role="listbox"
          aria-label="Preference pages"
          className={`absolute bottom-full left-0 z-20 mb-1 max-h-40 min-w-full overflow-y-auto rounded border ${collectionBuildBorderClassName} bg-white shadow-[0_8px_24px_rgba(0,0,0,0.12)]`}
        >
          {Array.from({ length: pageCount }, (_, index) => index + 1).map(
            pageNumber => (
              <button
                key={pageNumber}
                type="button"
                role="option"
                aria-selected={pageNumber === page}
                onClick={() => {
                  onChange(pageNumber);
                  setOpen(false);
                }}
                className={`flex w-full items-center border-b ${collectionBuildDividerClassName} px-2.5 py-1.5 text-left font-avenir text-xs text-secondary last:border-b-0 hover:bg-lightGray focus:outline-none ${
                  pageNumber === page ? "bg-lightGray font-medium" : ""
                }`}
              >
                {pageNumber}
              </button>
            )
          )}
        </div>
      ) : null}
    </div>
  );
};

type Props = {
  groups: PreferenceGroup[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  recommended?: boolean;
};

const preferenceImageClassName = (isSelected: boolean) =>
  `relative aspect-[3/4] w-full overflow-hidden rounded-lg ${
    isSelected
      ? "border-2 border-[#7A5240]"
      : `border-2 ${collectionBuildBorderClassName}`
  }`;

const PreferenceCard: React.FC<{
  item: PreferenceOption;
  isSelected: boolean;
  onToggle: (id: string) => void;
}> = ({ item, isSelected, onToggle }) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const hasPreview = Boolean(item.imageUrl);

  useEffect(() => {
    setIsImageLoaded(false);
  }, [item.imageUrl]);

  return (
    <button
      type="button"
      onMouseDown={event => event.preventDefault()}
      onClick={() => onToggle(item.id)}
      aria-pressed={isSelected}
      className="w-full text-left focus:outline-none"
    >
      <div className={preferenceImageClassName(isSelected)}>
        {(!hasPreview || !isImageLoaded) && (
          <Skeleton
            height="100%"
            width="100%"
            borderRadius={0}
            className="absolute inset-0 block leading-none"
          />
        )}
        {hasPreview ? (
          <img
            src={item.imageUrl}
            alt=""
            className={`h-full w-full object-cover transition-opacity duration-200 ${
              isImageLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setIsImageLoaded(true)}
            onError={() => setIsImageLoaded(true)}
          />
        ) : null}
        <span
          className={`absolute left-2 top-2 flex h-5 w-5 items-center justify-center rounded-[5px] border-2 ${
            isSelected
              ? "border-white bg-white"
              : "border-[#F2F1F6] bg-transparent"
          }`}
          aria-hidden
        >
          {isSelected ? (
            <svg viewBox="0 0 12 12" className="h-2.5 w-2.5">
              <path
                d="M2.1 6.2 4.8 8.7 9.9 3.3"
                fill="none"
                stroke="#1A1A1A"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : null}
        </span>
      </div>
    </button>
  );
};

const PreferenceGroupBlock: React.FC<{
  group: PreferenceGroup;
  selectedIds: string[];
  onToggle: (id: string) => void;
  gridClassName: string;
}> = ({ group, selectedIds, onToggle, gridClassName }) => (
  <section className="mt-4 first:mt-0">
    <div className={gridClassName}>
      {group.items.map(item => (
        <PreferenceCard
          key={`${group.id}-${item.id}`}
          item={item}
          isSelected={selectedIds.includes(item.id)}
          onToggle={onToggle}
        />
      ))}
    </div>
  </section>
);

const CollectionBuildPreferences: React.FC<Props> = ({
  groups,
  selectedIds,
  onToggle,
  recommended = false,
}) => {
  const [page, setPage] = useState(1);
  const indexed = useMemo(
    () =>
      groups.flatMap(group =>
        group.items.map(item => ({
          groupId: group.id,
          groupLabel: group.label,
          item,
        }))
      ),
    [groups]
  );
  const pageCount = Math.max(
    1,
    Math.ceil(indexed.length / PREFERENCE_PAGE_SIZE)
  );
  const pageRows = useMemo(() => {
    const start = (page - 1) * PREFERENCE_PAGE_SIZE;
    return indexed.slice(start, start + PREFERENCE_PAGE_SIZE);
  }, [indexed, page]);
  const pageGroups = useMemo(() => {
    const order: string[] = [];
    const byId = new Map<string, PreferenceGroup>();
    pageRows.forEach(row => {
      if (!byId.has(row.groupId)) {
        order.push(row.groupId);
        byId.set(row.groupId, {
          id: row.groupId,
          label: row.groupLabel,
          items: [],
        });
      }
      byId.get(row.groupId)?.items.push(row.item);
    });
    return order
      .map(id => byId.get(id))
      .filter((group): group is PreferenceGroup => Boolean(group));
  }, [pageRows]);
  const recommendedGroup = useMemo(() => {
    const selectedId = selectedIds[0];
    const source =
      groups.find(group => group.items.some(item => item.id === selectedId)) ||
      groups.find(group => group.items.length > 0);
    if (!source) {
      return null;
    }
    const item =
      source.items.find(entry => entry.id === selectedId) || source.items[0];
    if (!item) {
      return null;
    }
    return { ...source, items: [item] };
  }, [groups, selectedIds]);
  const visibleGroups = recommended
    ? recommendedGroup
      ? [recommendedGroup]
      : []
    : pageGroups;

  useEffect(() => {
    setPage(current => Math.min(current, pageCount));
  }, [pageCount]);

  return (
    <>
      {visibleGroups.map(group => (
        <PreferenceGroupBlock
          key={group.id}
          group={group}
          selectedIds={selectedIds}
          onToggle={onToggle}
          gridClassName={
            recommended
              ? preferenceRecommendedClassName
              : preferenceGridClassName
          }
        />
      ))}
      {recommended ? null : (
        <div className="mt-4 flex items-center justify-end gap-2 font-avenir text-xs leading-none text-[#737373]">
          <button
            type="button"
            aria-label="Previous preference page"
            disabled={page <= 1}
            onClick={() => setPage(current => Math.max(1, current - 1))}
            className={preferencePagerArrowClassName}
          >
            &lt;
          </button>
          <PreferencePageSelect
            page={page}
            pageCount={pageCount}
            onChange={setPage}
          />
          <span className="leading-none">of {pageCount}</span>
          <button
            type="button"
            aria-label="Next preference page"
            disabled={page >= pageCount}
            onClick={() => setPage(current => Math.min(pageCount, current + 1))}
            className={preferencePagerArrowClassName}
          >
            &gt;
          </button>
        </div>
      )}
    </>
  );
};

export default CollectionBuildPreferences;
